import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { extractErrorMessage } from '@/lib/api-client';
import { useDriversList } from '@/features/drivers/api';
import { useIndicateDriver } from './api';
import type { Fine } from './types';

interface IndicateDriverDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fine: Fine | null;
}

export function IndicateDriverDialog({ open, onOpenChange, fine }: IndicateDriverDialogProps) {
  const [driverId, setDriverId] = useState('');
  const [deadline, setDeadline] = useState('');

  const driversQuery = useDriversList({ limit: 100, active: 'true', sort: 'name' });
  const indicate = useIndicateDriver();

  useEffect(() => {
    if (open && fine) {
      setDriverId(fine.indicatedDriverId ?? fine.driverId ?? '');
      setDeadline(fine.indicationDeadline ? fine.indicationDeadline.slice(0, 10) : '');
    }
  }, [open, fine]);

  const canSubmit = driverId.length > 0 && !indicate.isPending;

  const handleSubmit = async () => {
    if (!fine) return;
    try {
      await indicate.mutateAsync({
        id: fine.id,
        driverId,
        indicationDeadline: deadline
          ? new Date(`${deadline}T23:59:59`).toISOString()
          : undefined,
      });
      toast.success('Condutor indicado');
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Indicar condutor</DialogTitle>
          <DialogDescription>
            O motorista indicado assumirá os pontos na CNH. Após a indicação, é preciso a
            assinatura dele para envio ao DETRAN.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="driver">Motorista *</Label>
            <Select value={driverId} onValueChange={setDriverId}>
              <SelectTrigger id="driver">
                <SelectValue placeholder="Selecione o motorista" />
              </SelectTrigger>
              <SelectContent>
                {(driversQuery.data?.data ?? []).map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name} · CNH {d.cnhCategory}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="deadline">Prazo para indicação (opcional)</Label>
            <Input
              id="deadline"
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {indicate.isPending ? 'Indicando…' : 'Indicar condutor'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
